// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { Rectangle } from '@shared/commons/Geometry';
import { DrawPointer, DrawPointerTypeEnum } from '../Utils/DrawPointer';
import { HistoryType } from '@shared/utils/History/HistoryType';
import { TerceraChartBaseRenderer } from './TerceraChartBaseRenderer';
import { Line, Pen, PenStyle, PolyLine, PolyRect, SolidBrush } from '@shared/commons/Graphics';
import { ThemeManager } from '@front/controls/misc/ThemeManager';
import { ColorStyleWidth, DynProperty, DynPropertyRelationType } from '@shared/commons/DynProperty';
import { TerceraChartDrawingType } from '../Utils/ChartConstants';
import { Resources } from '@shared/localizations/Resources';
import { getVolumeColoringModeKey, getVolumeColoringModeTooltipKey, VolumeColoringMode } from '@shared/utils/Volume/VolumeConstantsAndEnums';
import { VolumeBarsMovingAverage } from '@shared/utils/Volume/VolumeBarsMovingAverage';
import { ControlsUtils } from '@front/controls/UtilsClasses/ControlsUtils';
import { BarIndex } from '@shared/commons/cache/History/HistoryConstants';
import { LayersEnum } from '../Utils/LayersEnum';
import { VolumeFormatter } from '@shared/utils/Volume/VolumeFormatter';
import { createContextMenuItemsFromEnum } from '@shared/utils/ContextMenuUtils';
export class TerceraChartVolumeBarsRenderer extends TerceraChartBaseRenderer {
    constructor(cashSettings, chart) {
        super(chart);
        this.cashSettings = cashSettings;
        this.subscribeHistoryExpanded = false;
        this.showVolumeMarker = true;
        this.barLabelPaddingX = 8;
        this.barLabelPaddingY = 4;
        this.VolumeHeightPercent = 30;
        this.VolumeColoringMode = VolumeColoringMode.Fixed;
        this.volumeBarFont = ThemeManager.Fonts.Font_10F_regular;
        this.maxVol = Number.NEGATIVE_INFINITY;
        this.macAbortController = null;
        this.movingAverageCalculator = new VolumeBarsMovingAverage();
        this.movingAverageActive = false;
        this.movingAveragePeriod = 21;
        this.getInstrument = () => this.chart.Instrument();
        this.NoVolumeBarsData = () => this.maxVol <= 0;
        this.IsNeedDraw = (numberOfLayer) => this.assignLayer === numberOfLayer || LayersEnum.Quotes === numberOfLayer;
        this.cashSettings = cashSettings;
        this.SetClassName('TerceraChartVolumeBarsRenderer');
    }
    get Visible() {
        var _a, _b;
        return (_b = (_a = this.cashSettings) === null || _a === void 0 ? void 0 : _a.showVolume) !== null && _b !== void 0 ? _b : false;
    }
    set Visible(value) {
        super.Visible = value;
        if (this.cashSettings != null) {
            this.cashSettings.showVolume = value;
        }
    }
    SetMovingAveragePeriod(period) {
        this.movingAveragePeriod = period;
        this.movingAverageCalculator.setMovingAveragePeriod(period);
        if (this.subscribeHistoryExpanded) {
            this.RecalcMovingAverageMap();
        }
    }
    Draw(gr, window, windowsContainer, advParams = null) {
        var _a, _b, _c, _d;
        if (!this.Visible) {
            return;
        }
        const param = advParams;
        if ((param === null || param === void 0 ? void 0 : param.TerceraChart) == null) {
            return;
        }
        const isQuotesLayer = param.layerId === LayersEnum.Quotes;
        const chartDrawingType = param.TerceraChart.model.chartDrawingType;
        if (param.TerceraChart.TimeFrameInfo().Periods === 0 &&
            param.TerceraChart.TimeFrameInfo().HistoryType !== HistoryType.LAST) {
            return;
        }
        if (chartDrawingType === TerceraChartDrawingType.LinesBreak ||
            chartDrawingType === TerceraChartDrawingType.Renko ||
            chartDrawingType === TerceraChartDrawingType.Kagi ||
            chartDrawingType === TerceraChartDrawingType.TicTac) {
            return;
        }
        const cashItemSeries = param.TerceraChart.MainCashItemSeries;
        this.SubscribeRecalcMovingAverage(); // one-time subscription
        const clientRect = window.ClientRectangle.copy();
        const screenData = cashItemSeries.ChartScreenData.Storage;
        gr.save();
        gr.beginPath();
        gr.rect(clientRect.X, clientRect.Y, clientRect.Width, clientRect.Height);
        gr.clip();
        this.maxVol = Number.NEGATIVE_INFINITY;
        for (let kk = 0; kk < screenData.length; kk++) {
            if (screenData[kk].Volume > this.maxVol) {
                this.maxVol = screenData[kk].Volume;
            }
        }
        if (this.NoVolumeBarsData()) {
            gr.restore();
            return;
        }
        const scX = window.XScale;
        const adjustedBar = this.adjustedBarWidth(window);
        const lastVolumeK = (clientRect.Height * this.VolumeHeightPercent) / 100 / this.maxVol;
        const lastScreenDate = screenData[screenData.length - 1].Time;
        let curX = Math.ceil(window.PointsConverter.GetScreenXbyTime(lastScreenDate));
        const brushesPolyMap = {};
        const clientBottom = clientRect.Y + clientRect.Height;
        const polyPropName = 'rects';
        const drawPolyMethodName = 'DrawPolyRect';
        const volumeBarsLabels = [];
        const movingAverageLines = new PolyLine();
        for (let i = screenData.length - 1; i >= 0; i--) {
            if (screenData[i].NotShowExtendedSession === true) {
                curX -= scX;
                continue;
            }
            const currentVolume = screenData[i].Volume;
            const nextVolume = i > 0 ? screenData[i - 1].Volume : 0;
            const yy = clientBottom - Math.abs(currentVolume) * lastVolumeK;
            const barHeight = clientBottom - yy;
            if (barHeight === 0) {
                curX -= scX;
                continue;
            }
            let movingAverageValue = 0;
            if (this.movingAverageActive || this.VolumeColoringMode === VolumeColoringMode.ByMovingAverage) {
                movingAverageValue = (_b = (_a = this.movingAverageMap) === null || _a === void 0 ? void 0 : _a.get(screenData[i].BaseIntervalIndex)) !== null && _b !== void 0 ? _b : 0;
            }
            if (this.movingAverageActive) {
                const prevIndex = i > 0 ? i - 1 : 0;
                const prevValue = (_d = (_c = this.movingAverageMap) === null || _c === void 0 ? void 0 : _c.get(screenData[prevIndex].BaseIntervalIndex)) !== null && _d !== void 0 ? _d : 0;
                movingAverageLines.lines.push(new Line(curX - adjustedBar.width / 2, clientBottom - prevValue * lastVolumeK, curX + adjustedBar.width / 2, clientBottom - movingAverageValue * lastVolumeK));
            }
            const rect = new Rectangle(curX + adjustedBar.leftBorder / 2, yy, adjustedBar.width, barHeight);
            volumeBarsLabels.push({ bar: rect, volume: currentVolume });
            const currentBrushPropName = this.GetCurBrushPropName(screenData[i].Open, screenData[i].Close, nextVolume, currentVolume, movingAverageValue);
            if (!(currentBrushPropName in brushesPolyMap)) {
                brushesPolyMap[currentBrushPropName] = new PolyRect();
            }
            brushesPolyMap[currentBrushPropName][polyPropName].push(rect);
            curX -= scX;
            if (isQuotesLayer) {
                break;
            }
        }
        for (const brushPropName in brushesPolyMap) {
            gr[drawPolyMethodName](this[brushPropName], brushesPolyMap[brushPropName]);
        }
        if (this.movingAveragePen != null && this.movingAverageActive) {
            gr.DrawPolyLine(this.movingAveragePen, movingAverageLines);
        }
        this.RecalcMovingAverageMap();
        let priceScale = window.rightYScaleRenderer;
        if (!priceScale) {
            priceScale = window.leftYScaleRenderer;
        }
        this.DrawVolumeMarker(isQuotesLayer, cashItemSeries, clientBottom, lastVolumeK, param, priceScale.ScaleButtonOffset);
        this.DrawLabels(gr, volumeBarsLabels);
        gr.restore();
    }
    DrawLabels(gr, volumeBarsLabels) {
        volumeBarsLabels.forEach(({ bar, volume }) => {
            const strVolume = VolumeFormatter.getVolumeFormat(volume);
            const size = ControlsUtils.GetTextWidth(strVolume, this.volumeBarFont);
            if (size + this.barLabelPaddingX <= bar.Width &&
                this.volumeBarFont.Height + this.barLabelPaddingY <= bar.Height) {
                const x = bar.X + bar.Width / 2 - size / 2;
                const y = bar.Y + bar.Height / 2 - this.barLabelPaddingY;
                gr.DrawString(strVolume, this.volumeBarFont, this.volumeBarFontBrush, x, y);
            }
        });
    }
    DrawVolumeMarker(isQuotesLayer, cashItemSeries, clientBottom, lastVolumeK, advParams, scaleButtonOffset) {
        const cashItem = cashItemSeries.CashItem;
        const barIndex = cashItemSeries.Count() - cashItemSeries.rightPaddingBarsCount - 1; // last bar
        if (barIndex >= 0 && this.showVolumeMarker && isQuotesLayer) {
            let value = cashItemSeries.GetValue(barIndex, BarIndex.Volume);
            if (!(cashItem === null || cashItem === void 0 ? void 0 : cashItem.isRealTimeBuilding)) {
                value = cashItem.realTimeBar.Volume;
            }
            if (advParams != null) {
                const text = VolumeFormatter.getVolumeFormat(value);
                let yy = clientBottom - Math.abs(value) * lastVolumeK;
                if (clientBottom - yy - scaleButtonOffset < scaleButtonOffset) {
                    yy = clientBottom - scaleButtonOffset;
                }
                advParams.DrawPointers.push(new DrawPointer(DrawPointerTypeEnum.Indicator, NaN, this.GetCurrentBarBrush(cashItemSeries), text, undefined, undefined, undefined, yy));
            }
        }
    }
    GetCurrentBarBrush(cashItemSeries) {
        var _a, _b;
        const barIndex = cashItemSeries.Count() - 1;
        const curVolume = cashItemSeries.GetValue(barIndex, BarIndex.Volume);
        const prevBarVolume = this.VolumeColoringMode === VolumeColoringMode.ByDifference
            ? cashItemSeries.GetValue(barIndex - 1, BarIndex.Volume)
            : null;
        const needOpenAndClose = this.VolumeColoringMode === VolumeColoringMode.ByBar;
        const curOpen = needOpenAndClose ? cashItemSeries.GetValue(barIndex, BarIndex.Open) : null;
        const curClose = needOpenAndClose ? cashItemSeries.GetValue(barIndex, BarIndex.Close) : null;
        const needMovingAverage = this.VolumeColoringMode === VolumeColoringMode.ByMovingAverage;
        const MAValue = needMovingAverage ? ((_b = (_a = this.movingAverageMap) === null || _a === void 0 ? void 0 : _a.get(barIndex)) !== null && _b !== void 0 ? _b : 0) : null;
        return this[this.GetCurBrushPropName(curOpen, curClose, prevBarVolume, curVolume, MAValue)];
    }
    GetCurBrushPropName(open, close, nextVolume, currentVolume, movingAverageValue) {
        let current = 'volumeFixedBrush';
        if (this.VolumeColoringMode === VolumeColoringMode.Fixed) {
            // Do nothing, use the fixed brush
        }
        else if (this.VolumeColoringMode === VolumeColoringMode.ByBar) {
            if (open > close) {
                current = 'volumeDownBrush';
            }
            else if (open < close) {
                current = 'volumeUpBrush';
            }
        }
        else if (this.VolumeColoringMode === VolumeColoringMode.ByDifference) {
            if (currentVolume > nextVolume) {
                current = 'volumeUpBrush';
            }
            else if (currentVolume < nextVolume) {
                current = 'volumeDownBrush';
            }
        }
        else if (this.VolumeColoringMode === VolumeColoringMode.ByMovingAverage) {
            if (currentVolume > movingAverageValue) {
                current = 'volumeUpBrush';
            }
            else if (currentVolume < movingAverageValue) {
                current = 'volumeDownBrush';
            }
        }
        return current;
    }
    SubscribeRecalcMovingAverage() {
        var _a;
        const cashItemSeries = (_a = this.chart) === null || _a === void 0 ? void 0 : _a.MainCashItemSeries;
        if (!this.subscribeHistoryExpanded) {
            cashItemSeries === null || cashItemSeries === void 0 ? void 0 : cashItemSeries.HistoryExpanded.Subscribe(this.RecalcMovingAverageMap, this);
            this.subscribeHistoryExpanded = true;
            this.RecalcMovingAverageMap();
        }
    }
    ThemeChanged() {
        super.ThemeChanged();
        this.volumeFixedBrush = new SolidBrush(ThemeManager.colors.Chart_VolumeFixedColor);
        this.volumeUpBrush = new SolidBrush(ThemeManager.colors.Chart_VolumeUpColor);
        this.volumeDownBrush = new SolidBrush(ThemeManager.colors.Chart_VolumeDownColor);
        this.volumeBarFontBrush = new SolidBrush(ThemeManager.colors.Chart_VolumeBarFontColor);
        this.movingAverageLine = new ColorStyleWidth(ThemeManager.colors.Chart_VolumeMovingAverageLineColor, PenStyle.SimpleChart, 1);
        this.movingAveragePen = new Pen(this.movingAverageLine.Color, this.movingAverageLine.Width, this.movingAverageLine.Style);
    }
    RecalcMovingAverageMap() {
        var _a, _b;
        (_a = this.macAbortController) === null || _a === void 0 ? void 0 : _a.abort();
        this.macAbortController = new AbortController();
        const signal = this.macAbortController.signal;
        this.movingAverageCalculator
            .calculateMovingAverage((_b = this.chart) === null || _b === void 0 ? void 0 : _b.MainCashItemSeries, signal)
            .then((movingAverageMap) => {
            if (movingAverageMap !== null) {
                this.movingAverageMap = movingAverageMap;
            }
            else {
                // The calculation was aborted, handle accordingly (if needed)
                console.log('Moving Average Calculation Aborted');
            }
        })
            .catch((error) => {
            console.error('Error during moving average calculation:', error);
        });
    }
    Dispose() {
        var _a;
        const cashItemSeries = (_a = this.chart) === null || _a === void 0 ? void 0 : _a.MainCashItemSeries;
        if (cashItemSeries != null) {
            cashItemSeries.HistoryExpanded.UnSubscribe(this.RecalcMovingAverageMap, this);
        }
        super.Dispose();
    }
    Properties() {
        const properties = super.Properties();
        let prop = new DynProperty('showVolume', this.Visible, DynProperty.BOOLEAN, DynProperty.VOLUME_GROUP);
        prop.sortIndex = 10;
        prop.assignedProperty = [
            'VolumeHeightPercent',
            'VolumeOpacityPercent',
            'VolumeColoringMode',
            'VolumeFixedColor',
            'VolumeUpColor',
            'VolumeDownColor',
            'showVolumeMarker',
            'Volume.FontColor',
            'VolumeBars.MovingAverage.Active',
            'VolumeBars.MovingAverage.Period'
        ];
        prop.tooltip = Resources.getResource('property.VolumeBars.Visible.Tooltip');
        properties.push(prop);
        prop = new DynProperty('VolumeHeightPercent', this.VolumeHeightPercent, DynProperty.INTEGER, DynProperty.VOLUME_GROUP);
        prop.sortIndex = 20;
        prop.enabled = this.Visible;
        prop.minimalValue = 0;
        prop.maximalValue = 100;
        prop.decimalPlaces = 0;
        prop.tooltip = Resources.getResource('property.VolumeBars.PercentageHeight.Tooltip');
        properties.push(prop);
        prop = new DynProperty('VolumeColoringMode', this.VolumeColoringMode, DynProperty.COMBOBOX, DynProperty.VOLUME_GROUP);
        prop.sortIndex = 30;
        prop.enabled = this.Visible;
        prop.assignedProperty = ['VolumeUpColor', 'VolumeDownColor'];
        prop.DynPropertyRelationType = DynPropertyRelationType.Visibility;
        prop.tooltip = Resources.getResource('property.VolumeBars.SizeColoringScheme.Tooltip');
        prop.objectVariants = createContextMenuItemsFromEnum(VolumeColoringMode, getVolumeColoringModeKey, null, getVolumeColoringModeTooltipKey);
        properties.push(prop);
        prop = new DynProperty('VolumeFixedColor', this.volumeFixedBrush.Color, DynProperty.COLOR, DynProperty.VOLUME_GROUP);
        prop.sortIndex = 40;
        prop.enabled = this.Visible;
        prop.tooltip = Resources.getResource('property.VolumeBars.EqualColor.Tooltip');
        properties.push(prop);
        prop = new DynProperty('VolumeUpColor', this.volumeUpBrush.Color, DynProperty.COLOR, DynProperty.VOLUME_GROUP);
        prop.sortIndex = 50;
        prop.enabled = this.Visible;
        prop.tooltip = Resources.getResource('property.VolumeBars.PositiveColor.Tooltip');
        properties.push(prop);
        prop = new DynProperty('VolumeDownColor', this.volumeDownBrush.Color, DynProperty.COLOR, DynProperty.VOLUME_GROUP);
        prop.sortIndex = 60;
        prop.enabled = this.Visible;
        prop.tooltip = Resources.getResource('property.VolumeBars.NegativeColor.Tooltip');
        properties.push(prop);
        prop = new DynProperty('Volume.FontColor', this.volumeBarFontBrush.Color, DynProperty.COLOR, DynProperty.VOLUME_GROUP);
        prop.sortIndex = 65;
        prop.enabled = this.Visible;
        prop.tooltip = Resources.getResource('property.VolumeBars.FontColor.Tooltip');
        properties.push(prop);
        prop = new DynProperty('VolumeBars.MovingAverage.Active', this.movingAverageActive, DynProperty.BOOLEAN, DynProperty.VOLUME_GROUP);
        prop.sortIndex = 70;
        prop.enabled = this.Visible;
        prop.assignedProperty = ['VolumeBars.MovingAverage.Line'];
        prop.DynPropertyRelationType = DynPropertyRelationType.Visibility;
        prop.tooltip = Resources.getResource('property.VolumeBars.ShowMovingAverage.Tooltip');
        properties.push(prop);
        const MALine = this.movingAverageLine;
        prop = new DynProperty('VolumeBars.MovingAverage.Line', new ColorStyleWidth(MALine.Color, MALine.Style, MALine.Width), DynProperty.COLOR_STYLE_WIDTH, DynProperty.VOLUME_GROUP);
        prop.enabled = this.Visible;
        prop.sortIndex = 80;
        properties.push(prop);
        prop = new DynProperty('VolumeBars.MovingAverage.Period', this.movingAveragePeriod, DynProperty.INTEGER, DynProperty.VOLUME_GROUP);
        prop.minimalValue = 1;
        prop.sortIndex = 90;
        prop.enabled = this.Visible;
        prop.tooltip = Resources.getResource('property.VolumeBars.MAPeriod.Tooltip');
        properties.push(prop);
        prop = new DynProperty('showVolumeMarker', this.showVolumeMarker, DynProperty.BOOLEAN, DynProperty.VOLUME_GROUP);
        prop.sortIndex = 100;
        prop.enabled = this.Visible;
        prop.tooltip = Resources.getResource('property.VolumeBars.ShowValueMarketOnScale.Tooltip');
        properties.push(prop);
        return properties;
    }
    callBack(properties) {
        super.callBack(properties);
        let dp = DynProperty.getPropertyByName(properties, 'showVolume');
        if (dp != null)
            this.Visible = dp.value;
        dp = DynProperty.getPropertyByName(properties, 'VolumeHeightPercent');
        if (dp != null)
            this.VolumeHeightPercent = dp.value;
        dp = DynProperty.getPropertyByName(properties, 'VolumeFixedColor');
        if (dp != null)
            this.volumeFixedBrush.Color = dp.value;
        dp = DynProperty.getPropertyByName(properties, 'VolumeUpColor');
        if (dp != null)
            this.volumeUpBrush.Color = dp.value;
        dp = DynProperty.getPropertyByName(properties, 'VolumeDownColor');
        if (dp != null)
            this.volumeDownBrush.Color = dp.value;
        dp = DynProperty.getPropertyByName(properties, 'Volume.FontColor');
        if (dp != null)
            this.volumeBarFontBrush.Color = dp.value;
        dp = DynProperty.getPropertyByName(properties, 'VolumeColoringMode');
        if (dp != null)
            this.VolumeColoringMode = dp.value;
        dp = DynProperty.getPropertyByName(properties, 'VolumeBars.MovingAverage.Active');
        if (dp != null)
            this.movingAverageActive = dp.value;
        dp = DynProperty.getPropertyByName(properties, 'VolumeBars.MovingAverage.Line');
        if (dp != null) {
            const csw = dp.value;
            this.movingAverageLine.Color = csw.Color;
            this.movingAverageLine.Style = csw.Style;
            this.movingAverageLine.Width = csw.Width;
            this.movingAveragePen = new Pen(csw.Color, csw.Width, csw.Style);
        }
        dp = DynProperty.getPropertyByName(properties, 'VolumeBars.MovingAverage.Period');
        if (dp != null)
            this.SetMovingAveragePeriod(dp.value);
        dp = DynProperty.getPropertyByName(properties, 'showVolumeMarker');
        if (dp != null)
            this.showVolumeMarker = dp.value;
    }
    adjustedBarWidth(ww) {
        const MIN_BAR_WIDTH = 1;
        const barWidth = ww.XScale;
        let width = 1;
        let leftBorder = 0;
        if (barWidth < 2) {
            leftBorder = barWidth - MIN_BAR_WIDTH;
        }
        else if (barWidth < 10) {
            leftBorder = 1.5;
        }
        else {
            leftBorder = 2;
        }
        width = Math.max(barWidth - leftBorder, MIN_BAR_WIDTH);
        return { width, leftBorder };
    }
}
